<script>
import { Library } from "@observablehq/stdlib/";
import { D3Utility } from "../services/d3_utility.js";

export default {
  name: "LineChart",
  props: {
    data: {
      required: true,
      type: Array,
    },
    chartData: {
      required: true,
      type: Object,
    },
    new_data: {
      required: false,
      type: Array,
    },
    duration: {
      required: false,
      type: String,
    },
    restrictZoom: {
      required: false,
      type: Boolean,
    },
    isZoomed: {
      required: false,
      type: Object,
    },
    brushing: {
      required: false,
      type: Boolean,
    },
    no_zooming: {
      required: false,
      type: Boolean,
    },
    from_min: {
      required: false,
      type: Boolean,
    },
    prev_data: {
      required: false,
      type: Boolean,
    },
    show_events: {
      required: false,
      type: Boolean,
    },
    events_data: {
      required: false,
      type: Array,
    },
  },
  data() {
    return {
      isloading: true,
      xScale: null,
      line: null,
      yline: null,
      lines: null,
      events: null,
      bars: null,
      barWidth: null,
      yBarScale: null,
      yLineScale: null,
      gx: null,
      gy: null,
      gyb: null,
      svg: null,
      xaxis: null,
      yLineAxis: null,
      yBarAxis: null,
      min_translate_x: null,
      max_translate_x: null,
      width: 700,
      height: 300,
      margin: 30,
      padding: 30,
      bar_width: null,
      zoom: null,
      zoom_event: null,
      updated_data: null,
      clip: null,
      color: null,
      div: null,
      date_extent: null,
      data_extent: null,
      legends: null,
      computed_data: [],
      chart_config: null,
      thresholds: null,
      zoom_translate: null,
      marginFactor: null,
    };
  },
  watch: {
    data: function (newVal) {
      (!newVal || newVal.length == 0) && this.svg && this.svg.remove();
      if (newVal && newVal.length > 0 && this.chartData) {
        this.computed_data = this.data;
        this.initialize();
      }
    },
    chartData: function (newVal) {
      if (newVal && this.data && this.data.length > 0) {
        this.computed_data = this.data;
        this.initialize();
      }
    },
    restrictZoom: function (newVal) {
      if (newVal) {
        this.zoom &&
          this.zoom.translateExtent([
            [this.margin, -Infinity],
            [this.width - this.margin, Infinity],
          ]);
      }
    },
    duration: function (newVal) {
      if (newVal) {
        this.computed_data = this.data;
        this.initialize();
      }
    },
    show_events: function (newVal) {
      if (newVal && this.svg) {
        this.addEvents();
      } else if (!newVal && this.svg) {
        this.events.remove();
      }
    },
    isZoomed: function (newVal) {
      if (newVal && this.data && this.data.length > 0) {
        this.svg &&
          this.chartData &&
          this.chartData.id != newVal.id &&
          (!this.zoom_event || !this.isEventEqual(this.zoom_event.event, newVal.event));
        {
          this.zoomed(newVal.event, this);
        }
        this.zoom_event = newVal;
      }
    },
    new_data: function (newVal) {
      if (newVal && newVal.length > 0) {
        if (this.svg && this.zoom_event) {
          let date_entext = D3Utility.get_extent(newVal[0].values);
          this.computed_data.map((elem, i) => {
            elem.values =
              date_entext && this.data_extent && date_entext[1].getTime() <= this.data_extent[0].getTime()
                ? elem.values.concat(newVal[i].values)
                : newVal[i].values.concat(elem.values);
            return elem;
          });
          this.updateChart();
        }
      }
    },
  },
  mounted() {
    if (this.data && this.data.length > 0 && this.chartData) {
      this.computed_data = this.data;
      this.initialize();
    } else {
      this.isloading = false;
    }
  },
  methods: {
    initialize() {
      setTimeout(() => {
        if (this.svg) this.svg.remove();
        this.init();
      }, 1000);
    },
    init() {
      let self = this;
      let colorScheme = this.chartData.colors;
      const width = D3Utility.get_conatiner_width(".container");
      this.width = width;
      const height = 300;
      const legendHeight = 100;
      const margin = 30;
      const padding = 30;
      this.chart_config = {
        width,
        height,
        legendHeight,
        margin,
        padding,
      };
      this.zoom = D3Utility.define_zoom(this.chart_config, zoomed, zoom_end);

      if (this.restrictZoom) {
        this.zoom.translateExtent([
          [margin, -Infinity],
          [width - margin, Infinity],
        ]);
      }

      this.updated_data = {
        line: [],
        bar: [],
      };
      this.computed_data.map((elem) => {
        this.updated_data[elem.type].push(elem);
      });

      var keys =
        this.updated_data["bar"].map((elem) => {
          return elem.id;
        }) || [];

      this.updated_data["line"].map((elem) => {
        keys.push(elem.id);
      });
      let now = new Date();
      this.min_translate_x = 0;
      this.color = D3Utility.get_scale_ordinal(keys, colorScheme);
      let from_date = new Date();
      from_date.setDate(from_date.getDate() - parseInt(this.duration));
      this.xScale = D3Utility.get_utc_scale([margin, width - margin - padding]);
      this.yBarScale = D3Utility.get_linear_scale([height - margin - padding, margin]);
      this.yLineScale = D3Utility.get_linear_scale([height - margin - padding, margin]);
      this.date_extent = D3Utility.get_extent(this.computed_data[0].values);
      this.data_extent = D3Utility.get_extent(this.computed_data[0].values);
      this.duration_date = new Date(Date.now() - this.duration * 24 * 60 * 60 * 1000);
      let messages = 0;
      this.computed_data[0].values.map((elem) => {
        if (this.duration_date <= elem.key) messages++;
      });
      let date_inbetween = this.computed_data[0].values[30] && this.computed_data[0].values[30].key;
      if (messages >= 24 && this.duration_date <= this.date_extent[1] && this.duration_date >= this.date_extent[0]) {
        this.xScale.domain([new Date(this.duration_date), new Date(this.date_extent[1])]);
      } else if (messages <= 24 && this.duration_date >= this.date_extent[0] && date_inbetween) {
        this.xScale.domain([new Date(date_inbetween), new Date(this.date_extent[1])]);
      } else {
        this.xScale.domain([new Date(this.date_extent[0]), new Date(this.date_extent[1])]);
      }
      let min = D3Utility.get_min(
        this.chartData.yLineAxis ? [this.updated_data["line"][0]] : this.updated_data["line"]
      );
      let max = D3Utility.get_max(
        this.chartData.yLineAxis ? [this.updated_data["line"][0]] : this.updated_data["line"]
      );
      min = min - 0.15 * min;
      max = max + 0.15 * max;
      this.yLineScale.domain([this.from_min ? min : 0, max]).nice();
      min = this.chartData.yLineAxis ? D3Utility.get_min([this.updated_data["line"][1]]) : 0;
      max = D3Utility.get_max(this.chartData.yLineAxis ? [this.updated_data["line"][1]] : this.updated_data["bar"]);
      min = min == 0 ? 0 : min - 0.15 * min;
      max = max + 0.15 * max;

      this.yBarScale.domain([min, max]);

      //-----------------------------AXES-----------------------------//
      var dates = this.computed_data[0].values.map(function (v) {
        return v.key;
      });
      this.barWidth = D3Utility.get_scale_band(dates, [margin, width - margin - padding]);
      this.yBarAxis = (g, y) =>
        g
          .attr("class", "bar-axis")
          .attr("transform", `translate(${width - margin - padding},0)`)
          .style("stroke", "var(--v-titleLight-base)")
          .style("stroke-width", "0.5px")
          .call(D3Utility.define_right_axis(y));
      this.yLineAxis = (g, y) =>
        g
          .attr("class", "line-axis")
          .attr("transform", `translate(${margin + padding},0)`)
          .style("stroke", "var(--v-titleLight-base)")
          .style("stroke-width", "0.5px")
          .call(D3Utility.define_left_axis(y));
      var axisTimeFormat = D3Utility.get_time_format();
      this.xaxis = (g, x) =>
        g
          .attr("transform", `translate(${margin},${height - margin - padding})`)
          .style("stroke", "var(--v-titleLight-base)")
          .style("stroke-width", "0.5px")
          .call(D3Utility.define_bottom_axis(x, width, false, axisTimeFormat));
      this.div = D3Utility.append_tooltip();

      this.svg = D3Utility.create_svg(this.chartData.id, this.chart_config);

      const library = new Library();
      this.clip = library.DOM.uid("clip");

      this.svg = D3Utility.append_clip_path(this.chart_config, this.clip, this.svg);

      //----------------------------LINES-----------------------------//

      this.line = D3Utility.get_line_obj(this.xScale, this.yLineScale);
      this.yline = this.chartData.yLineAxis ? D3Utility.get_line_obj(this.xScale, this.yBarScale) : null;

      //-------------------------2. DRAWING---------------------------//
      //-----------------------------AXES-----------------------------//
      this.gx = D3Utility.append_axis(this.svg, this.xaxis, this.xScale, padding, height - 2 * padding);
      this.gx
        .append("text")
        .attr("x", "650px")
        .attr("y", "40px")
        .attr("text-anchor", "middle")
        .style("stroke", "var(--v-titleLight-base)")
        .style("stroke-width", "0.5px")
        .text(this.chartData.xAxis);

      this.gy = D3Utility.append_axis(this.svg, this.yLineAxis, this.yLineScale, 2 * padding, 0);
      this.gy
        .append("text")
        .attr("x", "-130px")
        .attr("y", "-45px")
        .attr("text-anchor", "middle")
        .attr("transform", "rotate(-90)")
        .style("stroke", "var(--v-titleLight-base)")
        .style("stroke-width", "0.5px")
        .text(this.chartData.yAxis);

      //----------------------------Thresholds-----------------------------//
      //To add threshold to the line chart, add 'min,max or nominal' value to the config file for specific line chart
      this.add_thresholds(this, this.xScale, this.yLineScale);

      if (this.updated_data["bar"].length > 0 || this.chartData.yLineAxis) {
        this.gyb = D3Utility.append_axis(this.svg, this.yBarAxis, this.yBarScale, 0, 0);
        this.gyb
          .append("text")
          .attr("x", "-130px")
          .attr("y", "50px")
          .attr("text-anchor", "middle")
          .attr("transform", "rotate(-90)")

          .text(this.chartData.yLineAxis ? this.chartData.yLineAxis : this.chartData.yBarAxis);
      }

      this.add_legends();

      this.max_translate_x = width - this.xScale(new Date(now)) - margin - margin;

      this.min_translate_x = -margin;

      if (this.brushing) D3Utility.brushing(this.svg, this.chart_config, this.selectionUpdated);
      if (!this.no_zooming && this.data) this.svg.call(this.zoom).transition().duration(750).call(this.zoom.scaleTo, 1);

      function zoomed() {}
      function zoom_end(event) {
        if (event && self.svg && self.chartData) {
          self.zoomed(event, self);
          self.$emit("zoomed", { event: event, id: self.chartData.id });
        }
      }
      this.isloading = false;
    },
    zoomed(event, self) {
      if (this.data && this.data.length > 0) {
        if (self.svg) self.updateChart(event);
      }
    },
    selectionUpdated(event) {
      if (event.type == "end" && event.selection)
        this.$emit("brush", [this.xScale.invert(event.selection[0]), this.xScale.invert(event.selection[1])]);
      if (event.type == "end" && !event.selection) this.$emit("clear_charts");
    },
    add_thresholds(self, xScale, yLineScale) {
      this.thresholds = this.svg
        .append("g")
        .attr("class", "thresholds")
        .attr("transform", "translate(" + this.chart_config.padding + ",0)");
      if (this.chartData.nom)
        this.thresholds
          .append("line")
          .attr("class", "nominal")
          .attr("clip-path", this.clip)
          .attr("x1", xScale.range()[0])
          .attr("x2", xScale.range()[1])
          .attr("y1", yLineScale(this.chartData.nom))
          .attr("y2", yLineScale(this.chartData.nom))
          .attr("stroke", "#bbc492")
          .attr("stroke-width", 2)
          .attr("opacity", 0.6)
          .on("mouseover", function (event) {
            self.div.transition().duration(200).style("opacity", 1);
            self.div
              .html(
                "<div class='primaryGrey--text caption'>Nominal</div><div class='font-weight-bold'>" +
                  self.chartData.nom +
                  (self.chartData.suffix ? self.chartData.suffix : "") +
                  "</div>"
              )
              .style("left", event.pageX - 100 + "px")
              .style("top", event.pageY - 120 + "px");
          })
          .on("mouseout", function () {
            self.div.transition().duration(500).style("opacity", 0);
          });
      if (this.chartData.max)
        this.thresholds
          .append("rect")
          .attr("class", "max")
          .attr("clip-path", this.clip)
          .attr("x", xScale.range()[0])
          .attr("width", this.chart_config.width)
          .attr("y", yLineScale(this.chartData.max) - this.chart_config.height)
          .attr("height", this.chart_config.height)
          .attr("stroke", "#d99a52")
          .attr("fill", "#d99a52")
          .attr("stroke-width", 2)
          .attr("opacity", 0.3)
          .on("mouseover", function (event) {
            self.div.transition().duration(200).style("opacity", 1);
            self.div
              .html(
                "<div class='primaryGrey--text caption'>Maximum Threshold</div><div class='font-weight-bold'>" +
                  self.chartData.max +
                  (self.chartData.suffix ? self.chartData.suffix : "") +
                  "</div>"
              )
              .style("left", event.pageX - 100 + "px")
              .style("top", event.pageY - 120 + "px");
          })
          .on("mouseout", function () {
            self.div.transition().duration(500).style("opacity", 0);
          });
      if (this.chartData.min)
        this.thresholds
          .append("rect")
          .attr("class", "min")
          .attr("clip-path", this.clip)
          .attr("x", xScale.range()[0])
          .attr("width", this.chart_config.width)
          .attr("y", yLineScale(this.chartData.min))
          .attr("height", this.chart_config.height)
          .attr("stroke", "#d99a52")
          .attr("fill", "#d99a52")
          .attr("stroke-width", 2)
          .attr("opacity", 0.3)
          .on("mouseover", function (event) {
            self.div.transition().duration(200).style("opacity", 1);
            self.div
              .html(
                "<div class='primaryGrey--text caption'>Minimum Threshold</div><div class='font-weight-bold'>" +
                  self.chartData.min +
                  (self.chartData.suffix ? self.chartData.suffix : "") +
                  "</div>"
              )
              .style("left", event.pageX - 100 + "px")
              .style("top", event.pageY - 60 + "px");
          })
          .on("mouseout", function () {
            self.div.transition().duration(500).style("opacity", 0);
          });
    },
    updateChart(event) {
      this.updated_data = {
        line: [],
        bar: [],
      };
      let ev = event ? event : this.zoom_event.event;
      let now = new Date();
      let currentDate = new Date();
      currentDate.setMinutes(currentDate.getMinutes() - 1);
      const xz = ev.transform.rescaleX(this.xScale);

      let line = D3Utility.get_line_obj(xz, this.yLineScale);
      let startTime = xz.invert(this.margin);
      let endTime = xz.invert(this.width - 2 * this.margin - 2 * this.padding);
      if (startTime != "Invalid Date" && endTime != "Invalid Date") {
        this.data_filtering(startTime, endTime);

        let min = D3Utility.get_min(
          this.chartData.yLineAxis ? [this.updated_data["line"][0]] : this.updated_data["line"],
          [startTime, endTime]
        );
        let max = D3Utility.get_max(
          this.chartData.yLineAxis ? [this.updated_data["line"][0]] : this.updated_data["line"],
          [startTime, endTime]
        );

        this.marginFactor = this.chartData.marginFactor ? this.chartData.marginFactor : 0.01;
        let mrgin = this.marginFactor * max;
        min = min - mrgin;
        max = max + mrgin;
        this.yLineScale.domain([this.from_min ? min : 0, max]).nice();

        let yline = this.chartData.yLineAxis ? D3Utility.get_line_obj(xz, this.yBarScale) : null;
        let yBarMargin = D3Utility.get_min(this.updated_data["bar"], [startTime, endTime]);
        yBarMargin = yBarMargin - yBarMargin * 0.01;

        if (this.chartData.yLineAxis) {
          min = D3Utility.get_min([this.updated_data["line"][1]], [startTime, endTime]);
          max = D3Utility.get_max([this.updated_data["line"][1]], [startTime, endTime]);
          mrgin = 0.01 * max;
          min = min - mrgin;
          max = max + mrgin;
          this.yBarScale.domain([this.from_min ? min : 0, max]);
        } else
          this.yBarScale.domain([
            this.from_min ? yBarMargin : 0,
            D3Utility.get_max(this.updated_data["bar"], [startTime, endTime]) || 10,
          ]);
        var dates = this.computed_data[0].values.map(function (v) {
          return v.key;
        });
        this.barWidth &&
          this.barWidth
            .domain(dates)
            .range([this.margin, this.width - this.margin - this.padding].map((d) => ev.transform.applyX(d)));
        this.bar_width = xz(now) - xz(currentDate);
        this.lines && this.lines.remove();
        this.bars && this.bars.remove();
        this.thresholds && this.thresholds.remove();

        if (this.show_events) {
          this.events && this.events.remove();
          this.addEvents();
        }
        this.chartData.yLineAxis ? this.addLines(xz, line, yline) : this.addLinesAndBars(xz, line);
        this.gx.call(this.xaxis, xz);
        this.gy.call(this.yLineAxis, this.yLineScale);
        this.gyb && this.gyb.call(this.yBarAxis, this.yBarScale);
        this.add_thresholds(this, xz, this.yLineScale);
        // this.legends && this.legends.remove();
        // this.add_legends();
      }
    },
    isEventEqual(object1, object2) {
      return (
        object1.transform.k === object2.transform.k &&
        object1.transform.x === object2.transform.x &&
        object1.transform.y === object2.transform.y
      );
    },
    data_filtering(startTime, endTime) {
      this.updated_data = {
        line: [],
        bar: [],
      };
      let min = this.date_extent && this.date_extent[0] ? new Date(this.date_extent[0]) : null;
      min && min.setDate(min.getDate() - (this.chartData.dataLimit || 14));
      let max = this.date_extent && this.date_extent[1] ? new Date(this.date_extent[1]) : null;
      max && max.setDate(max.getDate() + (this.chartData.dataLimit || 7));
      let today_limit = new Date().toJSON().split("T")[0].localeCompare(endTime.toJSON().split("T")[0]);

      if (this.data_extent[0] > startTime) {
        this.$emit("fetchData", ["start", this.data_extent[0]]);
      }
      if (today_limit == 1 && this.data_extent[1] < endTime) {
        this.$emit("fetchData", ["end", this.data_extent[1]]);
      }
      this.computed_data.map((elem) => {
        let temp_values = [];
        let temp_filtered_values = [];
        elem.values.map((a) => {
          a.key > min && a.key < max && temp_values.push(a);
          a.key > min && a.key < max && a.key > startTime && a.key < endTime && temp_filtered_values.push(a);
        });
        this.updated_data[elem.type].push({
          id: elem.id,
          label: elem.label,
          suffix: elem.suffix,
          type: elem.type,
          values: temp_filtered_values,
        });
        elem.values = temp_values;
      });

      this.data_extent = D3Utility.get_extent(this.computed_data[0].values);

      this.date_extent = D3Utility.get_extent(this.updated_data["line"][0].values);
    },
    addLinesAndBars(x_scale, lineObj) {
      this.bars = D3Utility.append_bars(
        this.svg,
        this.updated_data["bar"],
        this.chart_config,
        this.clip,
        this.bar_width,
        this.color,
        x_scale ? x_scale : this.xScale,
        this.yBarScale,
        this.div,
        this.chartData
      );

      this.lines = D3Utility.append_lines(
        this.svg,
        this.updated_data["line"],
        this.chart_config,
        this.clip,
        lineObj ? lineObj : this.line,
        this.color,
        x_scale ? x_scale : this.xScale,
        this.yLineScale,
        this.div,
        this.chartData
      );
    },
    add_legends() {
      var keys =
        this.updated_data["bar"].map((elem) => {
          return {
            id: elem.id,
            label: elem.label,
          };
        }) || [];

      this.updated_data["line"].map((elem) => {
        keys.push({
          id: elem.id,
          label: elem.label,
        });
      });
      // Add one dot in the legend for each name.
      this.legends = D3Utility.append_legends(
        this.svg,
        this.chartData.id,
        0,
        this.height,
        keys,
        this.chart_config,
        this.color
      );
    },
    addLines(x_scale, lineObj, yline) {
      this.bars = D3Utility.append_lines(
        this.svg,
        [this.updated_data["line"][1]],
        this.chart_config,
        this.clip,
        yline ? yline : this.yline,
        this.color,
        x_scale ? x_scale : this.xScale,
        this.yBarScale,
        this.div,
        this.chartData
      );

      this.lines = D3Utility.append_lines(
        this.svg,
        [this.updated_data["line"][0]],
        this.chart_config,
        this.clip,
        lineObj ? lineObj : this.line,
        this.color,
        x_scale ? x_scale : this.xScale,
        this.yLineScale,
        this.div,
        this.chartData
      );

      var keys = [];

      this.updated_data["line"].map((elem) => {
        keys.push({
          id: elem.id,
          label: elem.label,
        });
      });
      // Add one dot in the legend for each name.
      this.legends = D3Utility.append_legends(
        this.svg,
        this.chartData.id,
        0,
        this.height,
        keys,
        this.chart_config,
        this.color
      );
    },
    addEvents: function () {
      this.events = D3Utility.append_events(
        this.svg,
        this.events_data,
        this.chart_config,
        this.clip,
        this.color,
        this.xScale,
        this.div,
        this.chartData.id
      );
    },
  },
  computed: {},
};
</script>

<template>
  <div class="container">
    <v-skeleton-loader v-if="!chartData || !data || isloading" type="image"></v-skeleton-loader>
    <div class="pa-4 text-caption font-weight-bold primaryGrey--text" v-if="data.length == 0">No data available.</div>
    <div :id="`${chartData.id}`" class="svg-container"></div>
  </div>
</template>

<style lang="scss">
/* AXES */
/* ticks */
.axis line {
  stroke: #706f6f;
  stroke-width: 0.5;
  shape-rendering: crispEdges;
}

/* axis contour */
.axis path {
  stroke: #706f6f;
  stroke-width: 0.7;
  shape-rendering: crispEdges;
}

/* axis text */
.axis text {
  fill: #2b2929;
  font-family: "Roboto", sans-serif;
  // font-size: 120%;
  font-size: 0.8rem;
}

/* LINE CHART */
path.line-0 {
  fill: none;
  // stroke: #ed3700;
  stroke: url(#ptr);
}

path.line-1 {
  fill: none;
  stroke: #2b2929;
  stroke-dasharray: 2;
}

path.line-2 {
  fill: none;
  stroke: #9c9c9c;
  stroke-dasharray: 6;
}

.serie_label {
  fill: #2b2929;
  font-family: "Roboto", sans-serif;
  font-size: 80%;
}

.zoom {
  cursor: move;
  fill: none;
  pointer-events: all;
}

.bars rect {
  opacity: 0.4;
  stroke: black;
}

.pointer {
  cursor: pointer;
}

div.tooltip {
  position: absolute;
  width: 200px;
  height: 110px;
  background-color: white;
  padding: 10px;
  font-size: 12px;
  pointer-events: none;
  border: none;
  box-shadow:
    -3px -1px 20px 0 rgba(0, 0, 0, 0.18),
    0 6px 20px 0 rgba(153, 155, 168, 0.1);
}

div.tooltip > div {
  margin: 2px 0;
}

.caption {
  color: var(--v-primaryGrey-base);
}

.shadow,
path:hover {
  -webkit-filter: drop-shadow(6px 4px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(6px 4px 2px rgba(0, 0, 0, 0.7));
}

rect:hover {
  -webkit-filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
}

circle:hover {
  -webkit-filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
}

.hide {
  display: none;
}

.legend {
  cursor: pointer;
}

.legend.disabled > * {
  fill: var(--v-borderLight-base) !important;
}

.line-axis line {
  stroke: var(--v-borderLight-darken1);
  opacity: 0.8;
}

.line-axis path {
  stroke: var(--v-borderLight-darken1);
  opacity: 0.8;
}

.line-axis text {
  fill: var(--v-borderLight-darken1);
  opacity: 0.8;
  font-size: 0.8rem;
}

.line-axis.highlight line {
  stroke: var(--v-borderLight-darken4);
  opacity: 1;
}

.line-axis.highlight path {
  stroke: var(--v-borderLight-darken4);
  opacity: 1;
}

.line-axis.highlight text {
  fill: var(--v-borderLight-darken4);
  opacity: 1;
}

.bar-axis.highlight line {
  stroke: var(--v-borderLight-darken4);
  opacity: 1;
}

.bar-axis.highlight path {
  stroke: var(--v-borderLight-darken4);
  opacity: 1;
}

.bar-axis.highlight text {
  fill: var(--v-borderLight-darken4);
  opacity: 1;
}

.bar-axis line {
  stroke: var(--v-borderLight-base);
  opacity: 0.8;
}

.bar-axis path {
  stroke: var(--v-borderLight-base);
  opacity: 0.8;
}

.bar-axis text {
  fill: var(--v-borderLight-base);
  opacity: 0.8;
  font-size: 0.8rem;
}

.grid-line {
  stroke-dasharray: 4 2;
}

.events {
  .highlight {
    polygon {
      -webkit-filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.4));
      filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.4));
    }
  }

  .critical polygon,
  .critical text {
    stroke: var(--v-vtred-base);
    opacity: 1;
  }

  .warning polygon,
  .warning text {
    stroke: var(--v-yellow-base);
    opacity: 1;
  }

  .fault polygon,
  .fault text {
    stroke: var(--v-orange-base);
    opacity: 1;
  }

  .info polygon,
  .info text {
    stroke: var(--v-midnightBlue-base);
    opacity: 1;
  }
}
</style>
